// AUTO-GENERATED FILE - DO NOT EDIT
// This file was automatically generated by the XDK build tool.
// Any manual changes will be overwritten on the next generation.

/**
 * compliance client for the X API.
 *
 * This module provides a client for interacting with the compliance endpoints of the X API.
 */

import { Client, ApiResponse, RequestOptions } from '../client.js';
import { 
    Paginator, 
    PostPaginator, 
    UserPaginator, 
    EventPaginator
} from '../paginator.js';
import {
GetJobsResponse,
CreateJobsRequest,
CreateJobsResponse,
GetJobsByIdResponse,
} from './models.js';


/**
 * Options for getJobs method
 * 
 * @public
 */
export interface GetJobsOptions {
    
    
    /** Status of Compliance Job to list. 
     * Also accepts: status or proper camelCase (e.g., status) */
    status?: string;
    
    
    
    /** A comma separated list of ComplianceJob fields to display. 
     * Also accepts: compliance_job.fields or proper camelCase (e.g., complianceJobFields) */
    complianceJobFields?: Array<any>;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}



/**
 * Options for getJobsById method
 * 
 * @public
 */
export interface GetJobsByIdOptions {
    
    
    /** A comma separated list of ComplianceJob fields to display. 
     * Also accepts: compliance_job.fields or proper camelCase (e.g., complianceJobFields) */
    complianceJobFields?: Array<any>;
    
    
    
    /** Additional request options */
    requestOptions?: RequestOptions;
    /** Allow original API parameter names (e.g., 'tweet.fields', 'user.fields') and proper camelCase (e.g., 'tweetFields', 'userFields') */
    [key: string]: any;
}



/**
 * Client for compliance operations
 * 
 * This client provides methods for interacting with the compliance endpoints
 * of the X API. It handles authentication, request formatting, and response
 * parsing for all compliance related operations.
 * 
 * @category compliance
 */
export class ComplianceClient {
    private client: Client;

    /**
     * Creates a new compliance client instance
     * 
     * @param client - The main X API client instance
     */
    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Normalize options object to handle both camelCase and original API parameter names
     * Only accepts: proper camelCase (tweetFields) and original API format (tweet.fields)
     */
    private _normalizeOptions<T extends Record<string, any>>(options: T, paramMappings: Record<string, string>): T {
        if (!options || typeof options !== 'object') {
            return options;
        }
        
        const normalized: any = { ...options };
        
        // For each parameter mapping (original -> proper camelCase)
        for (const [originalName, camelName] of Object.entries(paramMappings)) {
            // Check if original format is used (e.g., 'tweet.fields', 'tweet_fields')
            if (originalName in normalized && !(camelName in normalized)) {
                normalized[camelName] = normalized[originalName];
                delete normalized[originalName];
            }
            // Also check for proper camelCase (e.g., 'tweetFields')
            // If it's already in proper camelCase, keep it (no conversion needed)
            // The camelName is already the proper camelCase format
        }
        
        return normalized as T;
    }



  /**
   * Get Compliance Jobs
   * Retrieves a list of Compliance Jobs filtered by job type and optional status.



   * @param type Type of Compliance Job to list.



   * @returns {Promise<GetJobsResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async getJobs(
        
        
        
        
        
        type: string,
        
        
        
        
        
        
        options: GetJobsOptions = {}
        
    ): Promise<GetJobsResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            
            
            'compliance_job.fields': 'complianceJobFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            status = undefined,
            
            
            
            complianceJobFields = [],
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/compliance/jobs';
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (type !== undefined) {
            
            params.append('type', String(type));
            
        }
        
        
        
        
        
        
        if (status !== undefined) {
            
            params.append('status', String(status));
            
        }
        
        
        
        
        
        
        if (complianceJobFields !== undefined && complianceJobFields.length > 0) {
            
            params.append('compliance_job.fields', complianceJobFields.join(','));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<GetJobsResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Create Compliance Job
   * Creates a new Compliance Job for the specified job type.



   * @param body Request body

   * @returns {Promise<CreateJobsResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async createJobs(
        
        
        
        
        
        
        body: CreateJobsRequest,
        
        
        
    ): Promise<CreateJobsResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        const requestOptions = {};
        

        // Build the path with path parameters
        let path = '/2/compliance/jobs';
        

        // Build query parameters
        const params = new URLSearchParams();
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            body: JSON.stringify(body || {}),
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            // No optional parameters, using empty request options
            
        };

        return this.client.request<CreateJobsResponse>(
            'POST',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }




  /**
   * Get Compliance Job by ID
   * Retrieves details of a specific Compliance Job by its ID.


   * @param id The ID of the Compliance Job to retrieve.




   * @returns {Promise<GetJobsByIdResponse>} Promise resolving to the API response
   */
    // Overload 1: Default behavior (unwrapped response)
    async getJobsById(
        
        
        
        id: string,
        
        
        
        
        
        
        
        
        options: GetJobsByIdOptions = {}
        
    ): Promise<GetJobsByIdResponse> {
        // Normalize options to handle both camelCase and original API parameter names
        
        
        const paramMappings: Record<string, string> = {
            
            
            'compliance_job.fields': 'complianceJobFields',
            
            
        };
        const normalizedOptions = this._normalizeOptions(options || {}, paramMappings);
        
        
        // Destructure options (exclude path parameters, they're already function params)
        const {
            
            
            complianceJobFields = [],
            
            
            
            requestOptions: requestOptions = {}
        } = normalizedOptions;
        

        // Build the path with path parameters
        let path = '/2/compliance/jobs/{id}';
        
        
        path = path.replace('{id}', encodeURIComponent(String(id)));
        
        

        // Build query parameters
        const params = new URLSearchParams();
        
        
        
        
        if (complianceJobFields !== undefined && complianceJobFields.length > 0) {
            
            params.append('compliance_job.fields', complianceJobFields.join(','));
            
        }
        
        
        

        // Prepare request options
        const finalRequestOptions: RequestOptions = {
            
            
            // Pass security requirements for smart auth selection
            security: [
                
                {
                    
                    'BearerToken': [],
                    
                }
                
            ],
            
            
            ...requestOptions
            
        };

        return this.client.request<GetJobsByIdResponse>(
            'GET',
            path + (params.toString() ? `?${params.toString()}` : ''),
            finalRequestOptions
        );
    }











}
